"use client";

import React, { useState, useEffect, useRef } from "react";
import { Search, Menu, X, ChevronDown, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { NavigationMenu, NavigationMenuList, NavigationMenuItem, NavigationMenuLink, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function SearchResults() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="flex items-center justify-center p-4 text-muted-foreground">
        <Loader className="animate-spin size-5 mr-2" />
        <span className="text-sm">Searching catalog...</span>
      </div>;
  }
  if (error) {
    return <p className="p-4 text-sm text-destructive">Unable to load results.</p>;
  }
  if (!products.length) {
    return <p className="p-4 text-sm text-muted-foreground">No products found.</p>;
  }
  return <div className="grid grid-cols-1 gap-2 max-h-[60vh] overflow-y-auto p-2">
      {products.slice(0, 5).map((product, i) => <Link key={product.id} to={product.link} className="flex items-center gap-3 p-2 rounded-md hover:bg-accent transition-colors group" data-index={i}>
          <div className="size-12 rounded bg-muted overflow-hidden shrink-0 border border-border">
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center bg-secondary/20 text-secondary-foreground text-xs">
                IMG
              </div>}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium truncate group-hover:text-primary transition-colors">
              {product.title}
            </h4>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {product.discounted_price ? <>
                  <span className="line-through opacity-70">${product.price}</span>
                  <span className="text-primary font-semibold">${product.discounted_price}</span>
                </> : <span>${product.price}</span>}
            </div>
          </div>
        </Link>)}
    </div>;
}
function SearchInterface({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-2xl animate-in slide-in-from-top-2 duration-200 z-40">
      <div className="container mx-auto px-4 py-4" ref={containerRef}>
        <div className="max-w-2xl mx-auto space-y-4">
          <div className="relative">
            <EcommerceProductsProvider wp_query={wp_query}>
              <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search for performance gear..." className="w-full" />
              {hasSearchQuery && <div className="mt-4 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
                  <SearchResults />
                </div>}
            </EcommerceProductsProvider>
          </div>
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground">
              Close Search
            </Button>
          </div>
        </div>
      </div>
    </div>;
}
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4 animate-pulse">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-16 bg-muted rounded" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent text-foreground hover:text-primary transition-colors font-medium text-sm uppercase tracking-wide">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] bg-popover border-border">
                    {item.children.map((child, j) => <li key={child.id} data-index={j}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-medium leading-none text-foreground group-hover:text-primary">
                              {child.label}
                            </div>
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group relative py-2 text-sm font-medium uppercase tracking-wide text-foreground transition-colors hover:text-primary">
                  {item.label}
                  <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return <div className="p-4 text-sm text-muted-foreground">Loading menu...</div>;
  return <nav className="flex flex-col space-y-4 p-6">
      {menuItems.map((item, i) => <div key={item.id} className="border-b border-border pb-4 last:border-0" data-index={i}>
          {item.children && item.children.length > 0 ? <div className="space-y-3">
              <span className="font-bold text-lg text-foreground">{item.label}</span>
              <div className="pl-4 flex flex-col space-y-2 border-l-2 border-muted">
                {item.children.map((child, j) => <Link key={child.id} to={child.href || "#"} className="text-muted-foreground hover:text-primary transition-colors" onClick={onClose} data-index={j}>
                    {child.label}
                  </Link>)}
              </div>
            </div> : <Link to={item.href || "#"} className="block text-lg font-bold text-foreground hover:text-primary transition-colors" onClick={onClose}>
              {item.label}
            </Link>}
        </div>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="header" className="relative w-full">
      <header className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md border-border/50 shadow-lg" : "bg-background border-transparent"}`}>
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          {}
          <div className="flex-shrink-0">
            <Link to="/" className="block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1564626/img/stridevault_logo.png" alt="StrideVault" className="h-7 w-auto object-contain" />
            </Link>
          </div>

          {}
          <div className="hidden lg:flex flex-1 justify-center px-8">
            <MenuProvider menu_id="53" className="w-full flex justify-center">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="icon" onClick={() => setIsSearchOpen(!isSearchOpen)} className={`hover:bg-accent hover:text-primary transition-colors ${isSearchOpen ? 'text-primary bg-accent' : ''}`} aria-label="Search">
              {isSearchOpen ? <X className="size-5" /> : <Search className="size-5" />}
            </Button>

            <CartIcon variant="ghost" size="icon" className="hover:bg-accent hover:text-primary transition-colors" />

            {}
            <Button variant="ghost" size="icon" className="lg:hidden hover:bg-accent hover:text-primary" onClick={() => setIsMobileMenuOpen(true)}>
              <Menu className="size-6" />
            </Button>
          </div>
        </div>

        {}
        <SearchInterface isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      </header>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-[60] lg:hidden">
          {}
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          
          {}
          <div className="absolute top-0 right-0 h-full w-[300px] bg-background border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="font-bold text-lg tracking-tight">Menu</span>
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} className="hover:text-destructive">
                <X className="size-5" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <MenuProvider menu_id="53">
                <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
            </div>

            <div className="p-4 border-t border-border bg-muted/20">
              <Link to="/CartPage" className="w-full" onClick={() => setIsMobileMenuOpen(false)}>
                <Button className="w-full justify-center" variant="default">
                  View Cart
                </Button>
              </Link>
            </div>
          </div>
        </div>}
    </section>;
}